/**
 * Utility functions for handling profile images, especially Google profile pictures
 */

/**
 * Optimizes Google profile picture URLs to reduce rate limiting
 * @param url - Original Google profile picture URL
 * @param size - Desired size (default: 96)
 * @returns Optimized URL or original URL if not a Google image
 */
export function optimizeGoogleProfilePicture(url: string, size: number = 96): string {
  if (!url || !url.includes('googleusercontent.com')) {
    return url;
  }

  try {
    // Remove existing size parameters and add our own
    const baseUrl = url.split('=s')[0];
    return `${baseUrl}=s${size}-c`;
  } catch {
    return url;
  }
}

/**
 * Creates a fallback URL for Google profile pictures
 * @param originalUrl - Original Google profile picture URL
 * @returns Fallback URL with different parameters
 */
export function createGoogleProfileFallback(originalUrl: string): string {
  if (!originalUrl || !originalUrl.includes('googleusercontent.com')) {
    return originalUrl;
  }

  try {
    // Try with different size and no crop parameter
    const baseUrl = originalUrl.split('=s')[0];
    return `${baseUrl}=s96`;
  } catch {
    return originalUrl;
  }
}

/**
 * Checks if an image URL is likely to have loading issues
 * @param url - Image URL to check
 * @returns true if the URL might have loading issues
 */
export function isProblematicImageUrl(url: string): boolean {
  if (!url) return false;
  
  // Google profile pictures are known to have rate limiting issues
  return url.includes('googleusercontent.com');
}

/**
 * Generates a deterministic color for user initials based on email or name
 * @param identifier - User email or name
 * @returns CSS color class
 */
export function getUserColor(identifier: string): string {
  if (!identifier) return 'bg-primary-600';
  
  const colors = [
    'bg-blue-600',
    'bg-green-600', 
    'bg-purple-600',
    'bg-pink-600',
    'bg-indigo-600',
    'bg-red-600',
    'bg-yellow-600',
    'bg-teal-600',
  ];
  
  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}